/**
 * Refund limits (GH-829, SPEC.md §12.1 and §12.3, DEC-006).
 *
 * A refund moves an organization's money back out. Below the limits one person holding
 * `donation.refund` may do it; above either limit the refund is **held** and a second, different
 * person must release it. The limits are platform settings — these are only what an untouched
 * database means, and they are DEC-006's recorded working assumption: USD 500.00 per refund and
 * USD 2,500.00 per organization per rolling 24 hours.
 *
 * "Above" is strict. A refund of exactly the single limit goes through; one minor unit more is
 * held. The daily limit counts what this refund would bring the window to, so the refund that
 * crosses the line is the one that waits — not the one after it.
 */
export const DEFAULT_REFUND_LIMITS = {
    singleRefundMinor: 50000n,
    perOrganizationPerDayMinor: 250000n,
};
/** The window the per-organization limit is counted over. Rolling, not a calendar day. */
export const REFUND_LIMIT_WINDOW_MS = 24 * 60 * 60 * 1000;
/**
 * Why a refund must wait for a second approver, or null when one person may do it.
 *
 * `refundedInWindowMinor` is what the organization has already refunded in the window, not
 * counting this refund. The single limit is checked first because it is the more specific answer:
 * a $900 refund on a quiet day was held for its size, not for the day.
 */
export function refundHoldReason(amountMinor, refundedInWindowMinor, limits) {
    if (amountMinor < 0n || refundedInWindowMinor < 0n) {
        throw new RangeError("refund amounts are never negative");
    }
    if (amountMinor > limits.singleRefundMinor)
        return "single_limit";
    if (refundedInWindowMinor + amountMinor > limits.perOrganizationPerDayMinor)
        return "daily_limit";
    return null;
}
/* ------------------------------------------- who approves a refund (GH-952, DEC-059) */
/**
 * The organization's own second-approval rule (owner, 2026-10-18), on top of the platform limits.
 *
 *  - `every` — a second person approves every refund. The default for every organization.
 *  - `above_amount` — a second person approves a refund **above** the organization's amount
 *    (strict, like the platform limits: a refund of exactly the amount runs at once).
 *  - `platform_limits` — only the platform limits hold a refund (the behaviour before GH-952).
 *
 * The platform limits are a ceiling no rule loosens: a refund is held when the rule OR a platform
 * limit says so.
 */
export const REFUND_APPROVAL_MODES = ["every", "above_amount", "platform_limits"];
export const DEFAULT_REFUND_APPROVAL_RULE = { mode: "every" };
/** The largest amount an organization may set: $100,000.00. Anything above is a typo. */
export const MAX_REFUND_APPROVAL_THRESHOLD_MINOR = 10000000n;
/**
 * Why a refund must wait for a second approver under the platform limits and the organization's
 * rule together, or null when one person may do it.
 *
 * A platform limit is named first when both apply: it is the hold the organization cannot change,
 * and it says more (a $900 refund under "every refund" waits for its size as well).
 */
export function refundHoldDecision(amountMinor, refundedInWindowMinor, limits, rule) {
    const platform = refundHoldReason(amountMinor, refundedInWindowMinor, limits);
    if (platform)
        return platform;
    if (rule.mode === "every")
        return "org_every_refund";
    if (rule.mode === "above_amount" && amountMinor > rule.thresholdMinor)
        return "org_above_amount";
    return null;
}
const STRICTNESS = {
    every: 2,
    above_amount: 1,
    platform_limits: 0,
};
/**
 * Whether moving from `before` to `after` lets more refunds through without a second person —
 * the change that needs a fresh step-up. A weaker mode, or a higher amount, loosens; a stronger
 * mode, a lower amount, or no change does not.
 */
export function loosensRefundApproval(before, after) {
    if (STRICTNESS[after.mode] !== STRICTNESS[before.mode]) {
        return STRICTNESS[after.mode] < STRICTNESS[before.mode];
    }
    if (before.mode === "above_amount" && after.mode === "above_amount") {
        return after.thresholdMinor > before.thresholdMinor;
    }
    return false;
}
/** The rule from its stored columns. A malformed pair (impossible under 0139's CHECK) is `every`. */
export function refundApprovalRule(mode, thresholdMinor) {
    if (mode === "platform_limits")
        return { mode };
    if (mode === "above_amount" && thresholdMinor !== null && thresholdMinor > 0n) {
        return { mode, thresholdMinor };
    }
    return { mode: "every" };
}
//# sourceMappingURL=refund-limits.js.map